import { throwIfAborted } from '@admin-panel/utils';
import axios, { AxiosResponse } from 'axios';
import type { IAsset } from '@admin-panel/types';
import { axiosInstance } from './clients/axios';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import {
  normalizeAssetUploadError,
  prepareAssetUploadFile,
  preparePackingVideoFile,
} from './assetuploadpreparation';
import { isDirectMp4 } from './mp4Compatibility';
import { PACKING_UPLOAD_MAX_BYTES, PACKING_UPLOAD_STALL_MS } from './packingUploadLimits';
import { uploadDeadline } from './uploadDeadline';
import { recordPackingTransferFailure, uncertainPackingTransfer } from './packingUploadFailure';
import { packingUploadDiagnostics } from './packingUploadDiagnostics';
import { assetDeviceCategory, type AssetUploadProvenance } from './assetUploadProvenance';
export { assetDeviceCategory } from './assetUploadProvenance';
export type { AssetUploadProvenance } from './assetUploadProvenance';

type UploadableAssetFile = File & {
  id?: string;
  url?: string;
  asset?: IAsset;
  uploadProvenance?: AssetUploadProvenance;
};

interface UploadAssetParams {
  file: UploadableAssetFile;
  onError?: (error: Error & { retryable?: boolean }) => void;
  onSuccess?: (file: UploadableAssetFile) => void;
  source?: string;
  usage: string;
  transfer?: AssetTransferOptions;
  packingVideo?: boolean;
}

export interface AssetUploadCheckpoint {
  path: string;
  uploaded: boolean;
  sourceId?: string;
  storageProvider?: 'AWS_S3';
  uploadToken?: string;
}

type UploadInstructions = AssetUploadCheckpoint & {
  url?: string;
  uploadMethod?: 'PUT' | 'POST';
  uploadHeaders?: Record<string, string>;
};

export interface AssetTransferOptions extends Partial<AssetUploadCheckpoint> {
  packingPackageId?: number;
  provenance?: AssetUploadProvenance;
  onCheckpoint?: (checkpoint: AssetUploadCheckpoint) => void;
  stallTimeoutMs?: number;
  signal?: AbortSignal;
  timeoutMs?: number;
  onProgress?: (percent: number) => void;
  onTransferProgress?: (progress: { loaded: number; total?: number; elapsedMs: number }) => void;
  onTransferComplete?: (sample: { bytes: number; elapsedMs: number }) => void;
  onStage?: (
    stage: 'initializing' | 'transferring' | 'confirming' | 'checking' | 'finishing',
  ) => void;
}

/** QC photos reuse the ordinary ORDER image route. Checkpoints deliberately contain no upload grant. */
export interface QcPhotoUploadCheckpoint {
  path: string;
  uploaded: boolean;
  assetId?: number;
}
export interface QcPhotoUploadOptions extends Partial<QcPhotoUploadCheckpoint> {
  packageId?: number;
  signal?: AbortSignal;
  onCheckpoint?: (checkpoint: QcPhotoUploadCheckpoint) => void;
  /** Deliberate same-file/same-grant retry only, after an exact-path finish check failed. */
  retryTransfer?: boolean;
}
export const QC_PHOTO_MAX_BYTES = 10_000_000;
export const QC_PHOTO_LIMIT = 3;
class QcPhotoAssetValidationError extends Error {
  constructor(
    message: string,
    readonly retryable = true,
  ) {
    super(message);
  }
}
// These two explicit Platform refusals cannot be repaired by retransmitting identical bytes.
// Verification/storage failures (3508/3509), readiness and transport remain unknown.
const permanentlyRefusedQcPhoto = (failure: unknown) => {
  const error = (failure ?? {}) as {
    statusCode?: number;
    status?: { code?: number };
    response?: { data?: { status?: { code?: number } } };
  };
  const code = Number(error.response?.data?.status?.code ?? error.status?.code ?? error.statusCode);
  return (
    (failure instanceof QcPhotoAssetValidationError && !failure.retryable) ||
    code === 3502 ||
    code === 3507
  );
};
const validQcPhotoPath = (path: unknown): path is string =>
  typeof path === 'string' &&
  path.length > 0 &&
  path.length <= 2048 &&
  !path.includes('://') &&
  !/[?#\r\n]/.test(path);

export const PACKING_VIDEO_UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;

// Older Platform MediaType resolves these non-video MIME types before consulting a suffix.
// Keep this fallback only for responses without authoritative uploadMethod instructions.
const legacyNonVideoMimeTypes = new Set([
  'application/octet-stream',
  'application/zip',
  'text/csv',
  'application/pdf',
  'application/dmg',
  'application/docx',
  'application/wav',
  'application/flac',
  'text/html',
  'application/json',
  'application/xls',
  'application/vnd.ms-excel',
  'image/jpg',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/svg+xml',
  'audio/mp3',
]);

// Diagnostic context only, never package authorization or proof of attachment.
// An existing checkpoint without context must remain historical/unknown.
const packingSourceId = (options?: AssetTransferOptions) => {
  if (options?.sourceId) return options.sourceId;
  const id = options?.packingPackageId;
  return !options?.path && typeof id === 'number' && Number.isSafeInteger(id) && id > 0
    ? `fulfillment/package/${id}`
    : undefined;
};

export class AssetUploaderClient {
  private readonly qcPhotoTransfers = new Map<
    string,
    { originalFile: File; uploadFile: File; instructions: UploadInstructions }
  >();

  /** Releases only browser-owned retry memory; never deletes an uploaded object or accepted evidence. */
  forgetQcPhotoUpload(path: string) {
    this.qcPhotoTransfers.delete(path);
  }

  async uploadQcPhoto(file: File | undefined, options: QcPhotoUploadOptions = {}): Promise<IAsset> {
    throwIfAborted(options.signal);
    if (
      file &&
      (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type.toLowerCase()) ||
        file.size <= 0 ||
        file.size > QC_PHOTO_MAX_BYTES)
    )
      throw new Error('Choose a JPG, PNG or WebP photo, 10 MB or smaller.');
    let path = options.path;
    if (path && !validQcPhotoPath(path)) throw new Error('The saved photo identity is invalid.');
    const completed = async (): Promise<IAsset> => {
      const asset = (await this.finishFileUpload(
        path!,
        options.signal,
        undefined,
        true,
      )) as IAsset & { path?: string };
      throwIfAborted(options.signal);
      let safeUrl = false;
      try {
        const url = new URL(asset?.url);
        safeUrl = url.protocol === 'https:' && !url.username && !url.password;
      } catch {
        /* invalid canonical response */
      }
      if (!asset)
        throw new Error('Photo completion could not be checked. Retry this same upload check.');
      if (
        !Number.isSafeInteger(asset.id) ||
        Number(asset.id) <= 0 ||
        asset.path !== path ||
        String(asset.type) !== 'IMAGE' ||
        asset.usage !== 'ORDER' ||
        !safeUrl
      )
        throw new QcPhotoAssetValidationError(
          'The photo service returned inconsistent asset details. Remove this selection and contact support before choosing another photo.',
          false,
        );
      if (asset.uploaded !== true || asset.ready !== true)
        throw new QcPhotoAssetValidationError(
          'Photo upload is not confirmed. Check this same upload again before continuing.',
        );
      this.qcPhotoTransfers.delete(path!);
      options.onCheckpoint?.({ path: path!, uploaded: true, assetId: asset.id });
      return asset;
    };
    if (path) {
      // A known identity is never replaced by another init, even after a lost response or reload.
      try {
        return await completed();
      } catch (failure) {
        throwIfAborted(options.signal);
        const retained = this.qcPhotoTransfers.get(path);
        if (
          failure instanceof QcPhotoAssetValidationError ||
          permanentlyRefusedQcPhoto(failure) ||
          !options.retryTransfer ||
          !file ||
          retained?.originalFile !== file
        )
          throw failure;
        await this.uploadFileToCloudflare(
          retained.instructions.url!,
          retained.uploadFile,
          {
            signal: options.signal,
            timeoutMs: PACKING_UPLOAD_STALL_MS,
            stallTimeoutMs: PACKING_UPLOAD_STALL_MS,
          },
          retained.instructions,
        );
        return completed();
      }
    }
    if (!file) throw new Error('Select a photo or recover its saved upload first.');
    const prepared = await prepareAssetUploadFile(file);
    throwIfAborted(options.signal);
    if (
      !['image/jpeg', 'image/png', 'image/webp'].includes(prepared.type.toLowerCase()) ||
      prepared.size <= 0 ||
      prepared.size > QC_PHOTO_MAX_BYTES
    )
      throw new Error('Prepared photos must be JPG, PNG or WebP, 10 MB or smaller.');
    const initialized: UploadInstructions = await this.initFileUpload(
      prepared,
      'ORDER',
      'ASSET',
      options.signal,
      {
        ...(options.packageId ? { sourceId: `fulfillment/package/${options.packageId}` } : {}),
      },
      true,
      knownPath => {
        path = knownPath;
        options.onCheckpoint?.({ path: knownPath, uploaded: false });
      },
    );
    throwIfAborted(options.signal);
    if (
      !validQcPhotoPath(initialized?.path) ||
      !initialized.url ||
      (initialized.uploadMethod && !['PUT', 'POST'].includes(initialized.uploadMethod))
    )
      throw new Error(
        'Photo transfer instructions were not confirmed. Check the saved upload, or remove this selection before choosing another photo.',
      );
    path = initialized.path;
    this.qcPhotoTransfers.set(path, {
      originalFile: file,
      uploadFile: prepared,
      instructions: initialized,
    });
    try {
      await this.uploadFileToCloudflare(
        initialized.url,
        prepared,
        {
          signal: options.signal,
          timeoutMs: PACKING_UPLOAD_STALL_MS,
          stallTimeoutMs: PACKING_UPLOAD_STALL_MS,
        },
        initialized,
      );
    } catch (failure) {
      throwIfAborted(options.signal);
      // A failed PUT reply does not prove missing bytes. One exact-path finish check, no automatic retransmit.
      try {
        return await completed();
      } catch (checkFailure) {
        // A positive contradictory canonical response is not hidden by the earlier lost PUT reply.
        if (permanentlyRefusedQcPhoto(checkFailure)) throw checkFailure;
        throw failure;
      }
    }
    return completed();
  }

  /** Bounded recorder upload; keep identity across finish/attachment retries. */
  async uploadRecordedVideo(
    file: File,
    options: AssetTransferOptions &
      Partial<AssetUploadCheckpoint> & {
        onCheckpoint?: (checkpoint: AssetUploadCheckpoint) => void;
      } = {},
  ): Promise<IAsset> {
    throwIfAborted(options.signal);
    file = preparePackingVideoFile(file);
    // Recorder/browser hints and chunk size limits are not a hard file-size guarantee.
    if (!(options.path && options.uploaded) && file.size > PACKING_UPLOAD_MAX_BYTES)
      throw new Error(
        'Packing videos must be 200 MB or smaller. Download this clip and select a smaller video.',
      );
    const diagnostics = packingUploadDiagnostics({
      packageId: options.packingPackageId,
      bytes: file.size,
      mimeType: file.type,
      ...options.provenance,
      signal: options.signal,
    });
    try {
      let path = options.path;
      if (path) diagnostics.checkpoint(path);
      let uploadToken = options.uploadToken;
      let provider = options.storageProvider;
      const sourceId = packingSourceId(options);
      // Negotiate conversion for other videos; a processing-disabled server still returns Stream.
      // Existing identities always resume their original provider, regardless of rollout flags.
      const uploadProtocol = path
        ? 'processed-video-v1'
        : (await isDirectMp4(file, options.signal))
          ? 'direct-mp4-v1'
          : 'processed-video-v1';
      if (!path || (provider === 'AWS_S3' && !options.uploaded)) {
        options.onStage?.('initializing');
        diagnostics.phase('INITIALIZATION');
        const initialized: UploadInstructions = await this.initFileUpload(
          file,
          'VIDEO',
          'ASSET',
          options.signal,
          {
            ...(uploadProtocol ? { uploadProtocol } : {}),
            pathPrefix: 'fulfillment/packing',
            ...(sourceId ? { sourceId } : {}),
            contentLength: file.size,
            ...options.provenance,
            ...(path ? { path, uploadToken } : {}),
          },
        );
        if (
          !initialized?.path ||
          (!initialized.uploaded && !initialized.url) ||
          (path && path !== initialized.path)
        )
          throw new Error('Video upload could not be initialized');
        path = initialized.path;
        diagnostics.checkpoint(path);
        uploadToken = initialized.uploadToken ?? uploadToken;
        provider = initialized.storageProvider ?? provider;
        const checkpoint = {
          path,
          uploaded: Boolean(initialized.uploaded),
          ...(sourceId ? { sourceId } : {}),
          ...(provider ? { storageProvider: provider, uploadToken } : {}),
        };
        options.onCheckpoint?.(checkpoint);
        if (!initialized.uploaded) {
          options.onStage?.('transferring');
          diagnostics.phase('TRANSFER');
          try {
            await this.uploadFileToCloudflare(
              initialized.url!,
              file,
              {
                ...options,
                path,
                timeoutMs: options.timeoutMs ?? PACKING_VIDEO_UPLOAD_TIMEOUT_MS,
                stallTimeoutMs: options.stallTimeoutMs ?? PACKING_UPLOAD_STALL_MS,
              },
              initialized,
              diagnostics,
            );
          } catch (error) {
            // A lost response or create-only conflict may follow a successful PUT.
            // Check once through existing exact-identity init/HEAD; never retransmit here.
            if (
              options.signal?.aborted ||
              provider !== 'AWS_S3' ||
              !uncertainPackingTransfer(error)
            )
              throw error;
            options.onStage?.('checking');
            let verified: UploadInstructions | undefined;
            try {
              verified = await this.initFileUpload(file, 'VIDEO', 'ASSET', options.signal, {
                uploadProtocol: 'processed-video-v1',
                pathPrefix: 'fulfillment/packing',
                ...(sourceId ? { sourceId } : {}),
                contentLength: file.size,
                path,
                uploadToken,
              });
            } catch {
              diagnostics.verification('CHECK_FAILED');
              // Preserve the original transfer error and retained checkpoint for manual recovery.
            }
            throwIfAborted(options.signal);
            if (verified?.uploaded !== true || verified.path !== path) {
              if (verified) diagnostics.verification('NOT_CONFIRMED');
              throw error;
            }
          }
        }
        throwIfAborted(options.signal);
        options.onCheckpoint?.({ ...checkpoint, uploaded: true });
      } else if (!options.uploaded) {
        // An interrupted direct POST is not proof of upload. Do not finish an empty asset.
        throw new Error('Video upload was interrupted. Retry uploading the retained clip.');
      }
      throwIfAborted(options.signal);
      options.onStage?.('finishing');
      diagnostics.phase('FINALIZATION');
      const asset = await this.finishFileUpload(path!, options.signal, uploadToken);
      throwIfAborted(options.signal);
      if (
        !(Number(asset?.id) > 0) ||
        asset?.uploaded !== true ||
        (!asset?.url && asset?.storageProvider !== 'AWS_S3')
      )
        throw new Error('Video upload completion was not confirmed');
      return asset;
    } catch (failure) {
      diagnostics.report(failure);
      throw failure;
    }
  }

  async uploadFile({
    file,
    onError,
    onSuccess,
    usage,
    source,
    transfer,
    packingVideo,
  }: UploadAssetParams) {
    let delegatedPackingVideo = false;
    const preparationDiagnostic =
      packingVideo && usage === 'VIDEO'
        ? packingUploadDiagnostics({
            packageId: transfer?.packingPackageId,
            bytes: file.size,
            mimeType: file.type,
            uploadMethod: 'FILE_UPLOAD',
            deviceCategory: file.uploadProvenance?.deviceCategory ?? assetDeviceCategory(),
            signal: transfer?.signal,
          })
        : undefined;
    try {
      // Freeze before preparation/I/O; reuse the same metadata if this file is retried.
      if (usage === 'VIDEO')
        file.uploadProvenance ??= {
          uploadMethod: 'FILE_UPLOAD',
          deviceCategory: assetDeviceCategory(),
        };
      if (!source) source = 'ASSET';
      const uploadFile = (await prepareAssetUploadFile(file)) as UploadableAssetFile;

      if (packingVideo && usage === 'VIDEO') {
        delegatedPackingVideo = true;
        const asset = await this.uploadRecordedVideo(uploadFile, {
          ...transfer,
          provenance: file.uploadProvenance,
        });
        uploadFile.url = asset.url;
        uploadFile.id = String(asset.id);
        uploadFile.asset = asset;
        onSuccess?.(uploadFile);
        return;
      }

      throwIfAborted(transfer?.signal);
      const sourceId = packingVideo ? packingSourceId(transfer) : undefined;
      const uploadRequestInfo = await this.initFileUpload(
        uploadFile,
        usage,
        source,
        transfer?.signal,
        {
          ...file.uploadProvenance,
          ...(sourceId ? { sourceId } : {}),
        },
      );
      await this.uploadFileToCloudflare(
        uploadRequestInfo.url,
        uploadFile,
        transfer,
        uploadRequestInfo,
      );
      throwIfAborted(transfer?.signal);
      const uploadedAssetInfo = await this.finishFileUpload(
        uploadRequestInfo.path,
        transfer?.signal,
      );
      throwIfAborted(transfer?.signal);
      uploadFile.url = uploadedAssetInfo.url;
      uploadFile.id = uploadedAssetInfo.id;
      onSuccess?.(uploadFile);
    } catch (error: unknown) {
      if (!delegatedPackingVideo) preparationDiagnostic?.report(error);
      onError?.(normalizeAssetUploadError(error));
    }
  }

  private async initFileUpload(
    file: UploadableAssetFile,
    usage: string,
    source?: string,
    signal?: AbortSignal,
    contract?: Record<string, unknown>,
    strict = false,
    onKnownPath?: (path: string) => void,
  ) {
    const payload = {
      usage: usage,
      type: file.type,
      name: file.name,
      source: source ? source : 'ASSET',
      ...contract,
    };
    const response: AxiosResponse = await axiosInstance.post(
      `${GATEWAY_API_URL}/asset/upload/init`,
      payload,
      signal ? { signal, timeout: 30000 } : {},
    );
    if (strict && validQcPhotoPath(response.data?.data?.path))
      onKnownPath?.(response.data.data.path);
    if (strict && (response.data?.success === false || response.data?.status?.type === 'ERROR'))
      throw new Error('Photo initialization was not confirmed.');
    return response.data?.data;
  }

  private async uploadFileToCloudflare(
    url: string,
    file: UploadableAssetFile,
    transfer?: AssetTransferOptions,
    instructions?: UploadInstructions,
    diagnostics?: ReturnType<typeof packingUploadDiagnostics>,
  ) {
    const axios_instance = axios.create();
    const started = performance.now();
    let loaded = 0;
    const deadline = uploadDeadline(transfer?.signal, transfer?.stallTimeoutMs);
    const onUploadProgress =
      transfer?.onProgress ||
      transfer?.onTransferProgress ||
      transfer?.onStage ||
      diagnostics ||
      transfer?.stallTimeoutMs
        ? (event: { loaded: number; total?: number }) => {
            loaded = Math.max(loaded, event.loaded);
            diagnostics?.progress(loaded);
            deadline.progress(event.loaded);
            transfer?.onTransferProgress?.({
              loaded: event.loaded,
              total: event.total,
              elapsedMs: performance.now() - started,
            });
            if (event.total) {
              transfer?.onProgress?.(Math.min(100, Math.floor((event.loaded / event.total) * 100)));
              if (event.loaded >= event.total) transfer?.onStage?.('confirming');
            }
          }
        : undefined;

    let response: AxiosResponse | undefined = undefined;

    try {
      if (
        instructions?.uploadMethod != null &&
        instructions.uploadMethod !== 'PUT' &&
        instructions.uploadMethod !== 'POST'
      )
        throw new Error('Unsupported upload transport');
      if (
        instructions?.storageProvider === 'AWS_S3' &&
        (instructions.uploadMethod !== 'PUT' ||
          !instructions.uploadToken ||
          !instructions.uploadHeaders?.['Content-Type'] ||
          instructions.uploadHeaders?.['If-None-Match'] !== '*')
      )
        throw new Error('Invalid AWS video upload instructions');

      // Older servers omit instructions. Match their media routing rather than business usage.
      const mime = file.type.toLowerCase();
      const legacyVideo =
        /^video\/(mp4|mkv|webm|mov|flv|avi|mpeg4)$/.test(mime) ||
        (!legacyNonVideoMimeTypes.has(mime) &&
          /\.(mp4|mov|webm|mkv|flv|avi|m4v)$/i.test(file.name));
      if (instructions?.uploadMethod ? instructions.uploadMethod === 'POST' : legacyVideo) {
        const formData = new FormData();
        formData.append('file', file);

        response = await axios_instance.post(url, formData, {
          signal: deadline.signal,
          ...(transfer?.timeoutMs ? { timeout: transfer.timeoutMs } : {}),
          onUploadProgress,
          headers: {
            'Content-Type': 'multipart/form-data',
            ...instructions?.uploadHeaders,
          },
        });
      } else {
        response = await axios_instance.put(url, file, {
          signal: deadline.signal,
          ...(transfer?.timeoutMs ? { timeout: transfer.timeoutMs } : {}),
          onUploadProgress,
          headers: {
            'Content-Type': file.type,
            ...instructions?.uploadHeaders,
          },
        });
      }
      throwIfAborted(transfer?.signal);
      transfer?.onTransferComplete?.({ bytes: file.size, elapsedMs: performance.now() - started });
      return response;
    } catch (error) {
      const failure = deadline.failure(error);
      if (transfer?.path && !transfer.signal?.aborted)
        recordPackingTransferFailure(failure, {
          packageId: transfer.packingPackageId,
          path: transfer.path,
          bytes: file.size,
          loaded,
          elapsedMs: Math.round(performance.now() - started),
        });
      throw failure;
    } finally {
      deadline.dispose();
    }
  }

  private async finishFileUpload(
    path: string,
    signal?: AbortSignal,
    uploadToken?: string,
    strict = false,
  ) {
    const payload = { path: path, ...(uploadToken ? { uploadToken } : {}) };
    const response: AxiosResponse = await axiosInstance.post(
      `${GATEWAY_API_URL}/asset/upload/finish`,
      payload,
      signal ? { signal, timeout: 30000 } : {},
    );
    if (strict && (response.data?.success === false || response.data?.status?.type === 'ERROR'))
      throw new Error('Photo completion was not confirmed. Check this same upload again.');
    return response.data?.data;
  }
}
